import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { login, ApiError } from "@/lib/api"

export function Login({ onNavigate }: { onNavigate: (page: "cli" | "admin") => void }) {
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [emailErr, setEmailErr] = useState(false)
  const [passErr, setPassErr] = useState(false)
  const [serverErr, setServerErr] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())
    setEmailErr(!validEmail)
    setPassErr(!password)
    setServerErr(null)
    if (!validEmail || !password) return
    setLoading(true)
    try {
      const data = await login(email.trim(), password)
      onNavigate(data.role === "admin" ? "admin" : "cli")
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setServerErr("Too many attempts. Please wait a minute and try again.")
      } else if (err instanceof ApiError && err.status === 403) {
        setServerErr("Your account has been suspended. Contact support.")
      } else {
        setServerErr(err instanceof ApiError ? err.message : "Login failed")
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-secondary flex flex-col items-center justify-center px-6">
      {/* Brand */}
      <div className="flex items-center gap-2 mb-5">
        <div className="flex h-7 w-7 items-center justify-center rounded-md bg-foreground flex-shrink-0">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
            <rect x="3" y="3" width="7" height="7" rx="1"/>
            <rect x="14" y="3" width="7" height="7" rx="1"/>
            <rect x="3" y="14" width="7" height="7" rx="1"/>
            <rect x="14" y="14" width="7" height="7" rx="1"/>
          </svg>
        </div>
        <span className="text-sm font-semibold tracking-tight">TwinModel</span>
      </div>

      {/* Card */}
      <div className="w-full max-w-sm bg-background border border-border rounded-xl shadow-sm px-8 py-8">
        <h1 className="text-xl font-bold tracking-tight text-center mb-1">Welcome back</h1>
        <p className="text-sm text-muted-foreground text-center mb-5">Sign in to your account</p>

        {/* Form */}
        <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium" htmlFor="email">Email</label>
            <Input
              id="email"
              type="email"
              placeholder="m@example.com"
              autoComplete="email"
              value={email}
              onChange={e => { setEmail(e.target.value); setEmailErr(false) }}
              className={emailErr ? "border-destructive focus-visible:ring-destructive/20" : ""}
            />
            {emailErr && <span className="text-xs text-destructive">Please enter a valid email address.</span>}
          </div>

          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium" htmlFor="password">Password</label>
              <a href="#" className="text-xs text-muted-foreground hover:text-foreground hover:underline underline-offset-2">Forgot your password?</a>
            </div>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={e => { setPassword(e.target.value); setPassErr(false) }}
              className={passErr ? "border-destructive focus-visible:ring-destructive/20" : ""}
            />
            {passErr && <span className="text-xs text-destructive">Password is required.</span>}
          </div>

          {serverErr && <span className="text-xs text-destructive text-center">{serverErr}</span>}
          <Button type="submit" className="w-full mt-1 rounded-full" disabled={loading}>
            {loading ? "Logging in…" : "Login"}
          </Button>
        </form>
      </div>

      {/* Legal */}
      <p className="text-xs text-muted-foreground text-center mt-5 max-w-xs leading-relaxed">
        By clicking continue, you agree to our{" "}
        <a href="#" className="underline underline-offset-2 hover:text-foreground">Terms of Service</a>{" "}
        and{" "}
        <a href="#" className="underline underline-offset-2 hover:text-foreground">Privacy Policy</a>.
      </p>

    </div>
  )
}
